import { localize } from "render/context/InternationalizationContext";

export const Trans = {
  Title: localize({
    en_GB: <>Do you consent to Neko Health accessing you journal?</>,
    sv_SE: <>Samtycker du till att Neko Health tar del av din journal?</>,
  }),
  Summary: localize({
    en_GB: (
      <>
        Neko Health will use your patient journal to assess your overall medical
        condition, to improve your experience with follow-up visits and
        consultations and to allow Neko Health to conduct research to identify
        health deviations.
      </>
    ),
    sv_SE: (
      <>
        Neko Health kommer använda din patientjournal för att göra en
        uppskattning av hela ditt hälsotillstånd, att förbättra upplevelsen vid
        återbesök och konsulationer, samt bedriva forskning och identifiera
        avvikelser.
      </>
    ),
  }),
  Validity: localize({
    en_GB: (
      <>
        This consent is valid for 12 months counting from the date it was
        consented to.
      </>
    ),
    sv_SE: <>Ditt samtycke är giltigt i 12 månader från det datum det gavs.</>,
  }),
  HowItWorks: localize({
    en_GB: <>How it works</>,
    sv_SE: <>Hur det fungerar</>,
  }),
  Background: localize({
    en_GB: <>Background</>,
    sv_SE: <>Bakgrund</>,
  }),
  BackgroundParagraph: localize({
    en_GB: (
      <>
        Neko Health AB and its subsidiaries HJN Proaktiv Hälsovård AB (“Atrium”)
        and NH Consumer Services AB (“NHC”) (together “Neko Health” “we” or
        “us”) are committed to the protection of your personal data.
        <br />
        <br />
        If you consent to us processing your personal data, including health
        data, we have the best opportunity to offer you the most safe,
        efficient, innovative and high-quality healthcare experience possible.
      </>
    ),
    sv_SE: (
      <>
        Neko Health AB och dess dotterbolag HJN Proaktiv Hälsovård AB (“Atrium”)
        och NH Consumer Services AB (“NHC”) (tillsammans “Neko Health” “vi”
        eller “oss”) har åtagit sig att skydda dina personuppgifter.
        <br />
        <br />
        Om du samtycker till att vi behandlar dina personuppgifter, inklusive
        hälsodata, har vi den bästa möjligheten att erbjuda dig den mest säkra,
        effektiva, innovativa och högkvalitativa vårdupplevelsen som möjligt.
      </>
    ),
  }),
  How: localize({
    en_GB: <> How your personal data is used</>,
    sv_SE: <>Hur dina personuppgifter används</>,
  }),
  HowParagraph: localize<{ href: string }>({
    en_GB: ({ href }) => (
      <>
        With your consent, Atrium will process your personal data, including
        health data in your patient journal, for the purposes of assessing
        information that may be of medical value and to facilitate follow-up
        consultation and for conducting research to identify health deviations.
        We use special equipment for carrying out the health checks which is why
        health checks with such equipment are part of a clinical study. A
        pre-requisite for offering health checks with special equipment is that
        we are able to use the personal data collected in this context for the
        purpose of developing our products and services in connection with the
        clinical studies which we are carrying out. For that reason, your
        consent also entails that Atrium shares your information with Neko
        Health AB and NHC. More specific information about testing and product
        development is available{" "}
        <a href={href} target="_blank" rel="noreferrer">
          here
        </a>
        .
      </>
    ),
    sv_SE: ({ href }) => (
      <>
        Med ditt samtycke kommer Atrium att behandla dina personuppgifter,
        inklusive hälsodata i din patientjournal, i syfte att bedöma information
        som kan vara av medicinskt värde och för att underlätta uppföljande
        konsultation och för att bedriva forskning för att identifiera
        hälsoavvikelser. Vi använder specialutrustning för att genomföra
        hälsokontrollerna varför hälsokontroller med sådan utrustning ingår i en
        klinisk studie. En förutsättning för att kunna erbjuda hälsokontroller
        med specialutrustning är att vi kan använda de personuppgifter som
        samlas in i detta sammanhang i syfte att utveckla våra produkter och
        tjänster i samband med de kliniska studier som vi genomför. Av den
        anledningen innebär ditt samtycke även att Atrium delar dina uppgifter
        med Neko Health AB och NHC. Mer specifik information om testning och
        produktutveckling finns tillgänglig{" "}
        <a href={href} target="_blank" rel="noreferrer">
          här
        </a>
        .
      </>
    ),
  }),
  Process: localize({
    en_GB: <>Personal data which are processed</>,
    sv_SE: <>Personuppgifter som omfattas av behandlingen</>,
  }),
  ProcessParagraph: localize({
    en_GB: (
      <>
        With your consent, we will process personal data and health data in your
        patient journal.
      </>
    ),
    sv_SE: (
      <>
        Med ditt samtycke kommer vi att behandla personuppgifter och
        hälsouppgifter i din patientjournal.
      </>
    ),
  }),
  Withdraw: localize({
    en_GB: <>Right to withdraw your consent</>,
    sv_SE: <>Rätt att återkalla ditt samtycke</>,
  }),
  WithdrawParagraph: localize({
    en_GB: (
      <>
        If you do not give your consent, we will not process your personal data
        in the manner described herein. You have the right to withdraw your
        consent at any time even if you have previously consented to this
        processing. Withdrawal of your consent does not affect the processing
        based on a previously given consent, but we will stop processing your
        personal data for this purpose after the withdrawal of your consent. You
        can withdraw your consent by changing your settings in the patient
        application or by contacting us.
      </>
    ),
    sv_SE: (
      <>
        Om du inte ger ditt samtycke kommer vi inte behandla dina
        personuppgifter på det sättet som beskrivs här. Även om du initialt gett
        ditt samtycke men ångrar dig har du rätt att när som helst återkalla
        ditt samtycke. Ett återkallande påverkar inte behandling som redan skett
        med stöd av ditt samtycke, men det innebär att vi inte kommer fortsätta
        att behandla uppgifterna för det ändamålet. Du kan återkalla ditt
        samtycke genom att ändra dina kontoinställningar i patientapplikationen
        eller kontakta oss.
      </>
    ),
  }),
  Specific: localize({
    en_GB: (
      <>
        Specific information about our processing of your personal data based on
        your consent
      </>
    ),
    sv_SE: (
      <>
        Särskild information om behandling av personuppgifter baserat på ditt
        samtycke
      </>
    ),
  }),
  SpecificParagraph: localize<{ href: string }>({
    en_GB: ({ href }) => (
      <>
        This information constitutes specific information about our processing
        of your personal data based on your consent and aims at providing
        sufficient information allowing you to give us an informed consent.
        General information about how we process your personal data is available
        in our privacy notice, available{" "}
        <a href={href} target="_blank" rel="noreferrer">
          here
        </a>
        .
      </>
    ),
    sv_SE: ({ href }) => (
      <>
        Denna information utgör särskild information om behandling av
        personuppgifter baserat på ditt samtycke och syftar till att ge dig
        tillräcklig information för att du ska kunna ge ett informerat samtycke.
        Information om hur vi behandlar dina personuppgifter i övrigt, inklusive
        hur du kan kontakta oss finns i vår informationstext, tillgänglig{" "}
        <a href={href} target="_blank" rel="noreferrer">
          här
        </a>
        .
      </>
    ),
  }),
};
